import { useState } from 'react';
import { Save, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

export default function ChangePassword() {
  const { api } = useAuth();

  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validatePassword = (password) => {
    if (password.length < 8) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/[a-z]/.test(password)) return false;
    if (!/\d/.test(password)) return false;
    if (!/[^A-Za-z0-9]/.test(password)) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.current_password || !formData.new_password || !formData.confirm_password) {
      toast.error('Preencha todos os campos.');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error('A confirmação da nova senha não confere.');
      return;
    }

    if (!validatePassword(formData.new_password)) {
      toast.error('A nova senha deve ter no mínimo 8 caracteres, com maiúscula, minúscula, número e caractere especial.');
      return;
    }

    setSaving(true);

    try {
      await api.post('/users/change-password', {
        current_password: formData.current_password,
        new_password: formData.new_password,
      });

      toast.success('Senha alterada com sucesso!');
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Erro ao alterar senha.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="change-password-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">
          Alterar Senha
        </h1>
        <p className="text-slate-500 mt-1">
          Atualize sua senha de acesso ao CRM.
        </p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Segurança da Conta
          </CardTitle>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Senha Atual</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={formData.current_password}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, current_password: e.target.value }))
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label>Nova Senha</Label>
              <div className="relative mt-1">
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, new_password: e.target.value }))
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <p className="text-xs text-slate-500 mt-2">
                Mínimo de 8 caracteres, com letra maiúscula, minúscula, número e caractere especial.
              </p>
            </div>

            <div>
              <Label>Confirmar Nova Senha</Label>
              <div className="relative mt-1">
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, confirm_password: e.target.value }))
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={saving}
                className="bg-brand-blue hover:bg-brand-blue-dark"
              >
                {saving ? (
                  'Salvando...'
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Alterar Senha
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}